import React, { useState } from 'react';
import { logger } from '../../utils/logger';
import { motion } from 'framer-motion';
import { 
  EnvelopeIcon,
  MapPinIcon,
  ClockIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';

const ContactUs: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    priority: 'medium'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Open user's email app with pre-filled information (bypasses form service limits)
      const emailBody = `Name: ${formData.name}%0D%0AEmail: ${formData.email}%0D%0APriority: ${formData.priority}%0D%0A%0D%0AMessage:%0D%0A${encodeURIComponent(formData.message)}`;
      const mailtoLink = `mailto:support@notescrafter.com?subject=${encodeURIComponent(`NotesCrafter Contact: ${formData.subject}`)}&body=${emailBody}`;
      
      // Open mailto link
      window.location.href = mailtoLink;
      
      // Show success message after a brief delay
      setTimeout(() => {
        setSubmitted(true);
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: '',
          priority: 'medium'
        });
        setIsSubmitting(false);
      }, 500);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open email app. Please try again.');
      logger.error('Contact form submission error:', err);
      setIsSubmitting(false);
    }
  };

  const contactMethods = [
    {
      icon: EnvelopeIcon,
      title: 'Email Support',
      description: 'Questions or feedback',
      contact: 'support@notescrafter.com',
      responseTime: 'Usually responds within 48 hours'
    },
    {
      icon: GlobeAltIcon,
      title: 'Social Media',
      description: 'Follow us for updates',
      contact: '@notescrafterapp',
      responseTime: 'Instagram, YouTube'
    }
  ];

  // offices removed per request

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-text-main mb-4">
            Contact Us
          </h1>
          <p className="text-xl text-text-muted max-w-2xl mx-auto">
            Have questions? We would love to hear from you. Send us a message and we will respond as soon as possible.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-glass/30 backdrop-blur-sm border border-border-glow rounded-lg p-8 hover:border-primary/50 transition-colors"
          >
            <h2 className="text-2xl font-bold text-text-main mb-6">Send us a message</h2>
            
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg"
              >
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-red-600 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <div>
                    <h3 className="text-sm font-semibold text-red-800">Error Sending Message</h3>
                    <p className="text-sm text-red-700 mt-1">{error}</p>
                  </div>
                </div>
              </motion.div>
            )}
            
            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-8"
              >
                <div className="w-16 h-16 bg-green-500/20 border border-green-500/40 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-text-main mb-2">Message Sent!</h3>
                <p className="text-text-muted">Thank you for contacting us. We will get back to you soon.</p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-text-main mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      autoComplete="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-text-main bg-glass/20 border border-border-glow rounded-md backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent placeholder:text-text-muted/60"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-text-main mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      autoComplete="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-text-main bg-glass/20 border border-border-glow rounded-md backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent placeholder:text-text-muted/60"
                      placeholder="your.email@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-text-main mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    autoComplete="off"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-text-main bg-glass/20 border border-border-glow rounded-md backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent placeholder:text-text-muted/60"
                    placeholder="What is this regarding?"
                  />
                </div>

                <div>
                  <label htmlFor="priority" className="block text-sm font-medium text-text-main mb-2">
                    Priority Level
                  </label>
                  <select
                    id="priority"
                    name="priority"
                    value={formData.priority}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-text-main bg-glass/20 border border-border-glow rounded-md backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="low">Low - General inquiry</option>
                    <option value="medium">Medium - Account or billing</option>
                    <option value="high">High - Technical issue</option>
                    <option value="urgent">Urgent - Service disruption</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-text-main mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    autoComplete="off"
                    required
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-text-main bg-glass/20 border border-border-glow rounded-md backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent placeholder:text-text-muted/60"
                    placeholder="Please describe your question or issue in detail..."
                  />
                </div>

                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`w-full py-3 px-6 rounded-xl font-bold transition-all duration-300 shadow-lg shadow-primary/20 hover:shadow-xl ${
                    isSubmitting
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 text-white'
                  }`}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </motion.button>
              </form>
            )}
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-8"
          >
            {/* Contact Methods */}
            <div className="bg-glass/30 backdrop-blur-sm border border-border-glow rounded-lg p-8 hover:border-primary/50 transition-colors">
              <h2 className="text-2xl font-bold text-text-main mb-6">Get in touch</h2>
              <div className="space-y-6">
                {contactMethods.map((method, index) => {
                  const IconComponent = method.icon;
                  return (
                    <div key={index} className="flex items-start space-x-4">
                      <div className="bg-primary/10 border border-primary/30 p-3 rounded-lg">
                        <IconComponent className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-text-main">{method.title}</h3>
                        <p className="text-text-muted text-sm mb-1">{method.description}</p>
                        <p className="font-medium text-primary">{method.contact}</p>
                        <p className="text-text-muted/70 text-sm flex items-center">
                          <ClockIcon className="w-4 h-4 mr-1 text-primary" />
                          {method.responseTime}
                        </p>
                      </div>
                    </div>
                  );
                })}
                
                {/* Physical Address */}
                <div className="flex items-start space-x-4 pt-4 border-t border-border-glow">
                  <div className="bg-primary/10 border border-primary/30 p-3 rounded-lg">
                    <MapPinIcon className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-text-main">Address</h3>
                    <address className="text-text-muted text-sm not-italic whitespace-pre-line">
                      NotesCrafter
                      <br />
                      www.notescrafter.com
                      
                    </address>
                  </div>
                </div>
              </div>
            </div>

            {/* Office locations removed per request */}

            {/* FAQ Link */}
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-6">
              <h3 className="font-semibold text-text-main mb-2">Looking for quick answers?</h3>
              <p className="text-text-muted text-sm mb-4">
                Check out our FAQ section for instant answers to common questions.
              </p>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => window.dispatchEvent(new CustomEvent('navigateToPage', { detail: { page: 'help-center' } }))}
                aria-label="Visit Help Center"
                className="text-primary font-medium hover:underline"
              >
                Visit Help Center →
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;